import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  MAX_DISPLAY_NAME_LENGTH,
  PROMPT_MANAGEMENT_CATALOG,
  toneProfiles,
  type ToneProfile,
} from 'skipify-types';
import { v4 as uuidv4 } from 'uuid';

import { Button } from '../../../components/core';
import Switch from '../../../common/Switch';
import { AgentSettingsModal } from '../components/AgentSettingsModal';
import { AgentStoreReadinessNotice } from '../components/AgentStoreReadinessNotice';
import { DeleteAgentModal } from '../components/DeleteAgentModal';
import { useMerchant } from '../../../contexts/MerchantContext';
import {
  useDeleteAgenticPersonalityMutation,
  useGetAgenticSettingsQuery,
  useGetAgenticVoiceProfilesQuery,
  usePatchAgenticSettingsMutation,
  usePreviewAgenticVoiceMutation,
  useUploadAgenticAvatarMutation,
} from '../../../store/modules/merchant/api';
import { toast } from '../../../util/toast';
import LogoInput from '../../Branding/LogoInput';
import {
  AGENT_AVATARS,
  CUSTOM_AVATAR_OPTION_ID,
  DEFAULT_AGENT_VOICE_PROFILE_ID,
  MAX_AGENTS,
  VOICE_SAMPLE_MAX_LENGTH,
  avatarLabelForSrc,
  isCatalogAvatarSrc,
  isCustomAvatarSrc,
  uploadedAvatarSrcFromResponse,
  voiceLabelForProfileId,
  voicesFromProfileIds,
} from '../agentCatalog';
import { EllaPersonalityDraft } from '../ellaDraft';
import { useEllaDraft } from '../EllaDraftContext';
import { isAgenticStoreReady } from '../storeReadiness';
import { useProvisioningPlatform } from '../provisioning';
import { toAgentSelector } from '../agentSelector';
import { AgentSelectorThemeSection } from '../components/AgentSelectorThemeSection';
import { exampleBoxSx, selectionBoxSx } from '../ellaFormStyles';

const EXAMPLE_BOX_SX = exampleBoxSx('#F5F5F5');

/** Portrait avatars are taller than the circle; crop from the bottom so the head stays in frame. */
const avatarCropSx = {
  objectFit: 'cover' as const,
  objectPosition: 'center top',
};

const createButtonSx = {
  height: 48,
  borderRadius: '51px',
  textTransform: 'none' as const,
  fontFamily: 'Poppins, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.4,
  boxShadow: 'none',
  width: '100%',
  color: 'primary.dark',
  bgcolor: 'background.paper',
  borderColor: 'primary.light',
  '&:hover': {
    bgcolor: 'background.paper',
    borderColor: 'primary.main',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: 'grey.400',
    borderColor: 'grey.300',
    bgcolor: 'background.paper',
  },
};

const agentActionButtonSx = {
  height: 48,
  minWidth: 125,
  px: 2,
  flexShrink: 0,
  whiteSpace: 'nowrap',
} as const;

const capitalize = (value?: string | null) => {
  if (!value) {
    return '';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatToneLabel = (tone: ToneProfile) => capitalize(tone);

const defaultVoiceSample = (displayName: string): string => {
  const name = displayName.trim();
  const text = name
    ? `Hi, I'm ${name}, your shopping advisor. What can I help you with today?`
    : "Hi, I'm your shopping advisor. What can I help you with today?";
  return text.slice(0, VOICE_SAMPLE_MAX_LENGTH);
};

const isAutoGeneratedVoiceSample = (sample: string): boolean =>
  sample === defaultVoiceSample('') ||
  (sample.startsWith("Hi, I'm ") && sample.includes(', your shopping advisor'));

const emptyPersonality = (voiceProfileId: string): EllaPersonalityDraft => ({
  clientId: uuidv4(),
  isNew: true,
  displayName: '',
  avatarSrc: '',
  tone: 'warm-assured',
  voiceProfileId,
  enabled: false,
});

const AvatarCircle = ({
  src,
  size,
  alt = '',
  placeholder = false,
}: {
  src?: string;
  size: number;
  alt?: string;
  placeholder?: boolean;
}): JSX.Element => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      flexShrink: 0,
      bgcolor: 'grey.200',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {src ? (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: '100%', height: '100%', ...avatarCropSx }}
      />
    ) : placeholder ? (
      <AutoAwesomeIcon sx={{ fontSize: size * 0.34, color: 'brand.main' }} aria-hidden />
    ) : null}
  </Box>
);

const getSaveErrorMessage = (err: unknown, fallback: string): string => {
  const errors = (err as { data?: { errors?: string[] } })?.data?.errors;
  return Array.isArray(errors) && errors.length > 0 ? errors.join(' ') : fallback;
};

export const AgentsTab = (): JSX.Element => {
  const { merchantId } = useMerchant();
  const { data: agenticSettings } = useGetAgenticSettingsQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const storeReadiness = agenticSettings?.storeReadiness;
  const { requiresProvisioning } = useProvisioningPlatform();
  const storeReady = isAgenticStoreReady(storeReadiness, { hasProvisioning: requiresProvisioning });
  const {
    draft,
    createMode,
    readOnly,
    updateDraft,
    draftResetCount,
    removePersonality,
    discardPersonality,
  } = useEllaDraft();
  const [patchAgenticSettings, { isLoading: isSavingEnabled }] = usePatchAgenticSettingsMutation();
  const [deleteAgenticPersonality, { isLoading: isDeletingAgent }] =
    useDeleteAgenticPersonalityMutation();
  const [previewAgenticVoice, { isLoading: isPreviewingVoice }] = usePreviewAgenticVoiceMutation();
  const [uploadAgenticAvatar, { isLoading: isUploadingAvatar }] = useUploadAgenticAvatarMutation();
  const { data: voiceProfiles } = useGetAgenticVoiceProfilesQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const catalogVoices = useMemo(
    () => voicesFromProfileIds(voiceProfiles?.voiceProfileIds),
    [voiceProfiles?.voiceProfileIds],
  );
  const defaultVoiceProfileId = catalogVoices[0]?.profileId ?? DEFAULT_AGENT_VOICE_PROFILE_ID;
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  const personalities = useMemo(() => draft.personalities ?? [], [draft.personalities]);
  const [selectedClientId, setSelectedClientId] = useState(personalities[0]?.clientId ?? '');
  const [voiceSampleByClientId, setVoiceSampleByClientId] = useState<Record<string, string>>({});
  const [customAvatarByClientId, setCustomAvatarByClientId] = useState<Record<string, boolean>>({});
  const [replaceAvatarOpenByClientId, setReplaceAvatarOpenByClientId] = useState<
    Record<string, boolean>
  >({});
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(false);

  const selected =
    personalities.find((personality) => personality.clientId === selectedClientId) ??
    personalities[0];

  useEffect(() => {
    if (
      selectedClientId &&
      personalities.some((personality) => personality.clientId === selectedClientId)
    ) {
      return;
    }
    setSelectedClientId(personalities[0]?.clientId ?? '');
  }, [personalities, selectedClientId]);

  useEffect(() => {
    setPendingEnabled(selected?.enabled ?? false);
  }, [selected?.enabled]);

  useEffect(() => {
    setCustomAvatarByClientId({});
    setReplaceAvatarOpenByClientId({});
  }, [draftResetCount]);

  useEffect(() => {
    return () => {
      previewAudioRef.current?.pause();
      if (previewAudioRef.current?.src) {
        URL.revokeObjectURL(previewAudioRef.current.src);
      }
    };
  }, []);

  const voiceSample = selected
    ? (voiceSampleByClientId[selected.clientId] ?? defaultVoiceSample(selected.displayName))
    : '';

  const selectedIsCustomAvatar = Boolean(
    selected &&
    (customAvatarByClientId[selected.clientId] || isCustomAvatarSrc(selected.avatarSrc)),
  );
  const avatarSelectValue = selected
    ? (AGENT_AVATARS.find((avatar) => avatar.src === selected.avatarSrc)?.src ??
      (selectedIsCustomAvatar ? CUSTOM_AVATAR_OPTION_ID : ''))
    : '';
  const showCustomAvatarUploader = Boolean(
    selected &&
    selectedIsCustomAvatar &&
    (replaceAvatarOpenByClientId[selected.clientId] || !selected.avatarSrc),
  );

  const voiceOptions = useMemo(() => {
    const options = catalogVoices.map((voice) => ({
      profileId: voice.profileId,
      label: voice.label,
    }));
    if (
      selected?.voiceProfileId &&
      !options.some((option) => option.profileId === selected.voiceProfileId)
    ) {
      options.push({
        profileId: selected.voiceProfileId,
        label: voiceLabelForProfileId(selected.voiceProfileId, catalogVoices),
      });
    }
    return options;
  }, [catalogVoices, selected?.voiceProfileId]);

  const atLimit = personalities.length >= MAX_AGENTS;
  const hasLiveAgent = personalities.some((personality) => personality.enabled);
  const liveAgentCount = personalities.filter((personality) => personality.enabled).length;
  const showCreateButton = !readOnly && !createMode;
  const canCreate = showCreateButton && !atLimit;
  const toneDescription = selected
    ? (PROMPT_MANAGEMENT_CATALOG.toneProfiles.descriptions[selected.tone] ?? '')
    : '';
  const selectedIsLive = selected?.enabled ?? false;
  const canToggleLive = Boolean(
    selected?.personalityId && !selected.isNew && !readOnly && (storeReady || selectedIsLive),
  );
  const canDelete = Boolean(
    selected &&
    !readOnly &&
    !createMode &&
    (selected.isNew || (selected.personalityId && !selectedIsLive)),
  );
  const deleteDisabled = !canDelete;

  const updateSelected = (patch: Partial<EllaPersonalityDraft>) => {
    if (!selected) {
      return;
    }
    const clientId = selected.clientId;
    updateDraft((prev) => ({
      ...prev,
      personalities: (prev.personalities ?? []).map((personality) =>
        personality.clientId === clientId ? { ...personality, ...patch } : personality,
      ),
    }));
  };

  const onAvatarOptionChange = (value: string) => {
    if (!selected) {
      return;
    }
    const clientId = selected.clientId;
    if (value === CUSTOM_AVATAR_OPTION_ID) {
      setCustomAvatarByClientId((prev) => ({ ...prev, [clientId]: true }));
      if (isCatalogAvatarSrc(selected.avatarSrc)) {
        updateSelected({ avatarSrc: '' });
        setReplaceAvatarOpenByClientId((prev) => ({ ...prev, [clientId]: true }));
        return;
      }
      setReplaceAvatarOpenByClientId((prev) => ({
        ...prev,
        [clientId]: !isCustomAvatarSrc(selected.avatarSrc),
      }));
      return;
    }
    setCustomAvatarByClientId((prev) => ({ ...prev, [clientId]: false }));
    setReplaceAvatarOpenByClientId((prev) => ({ ...prev, [clientId]: false }));
    updateSelected({ avatarSrc: value });
  };

  const handleCustomAvatarFile = async (file: File) => {
    if (!merchantId || !selected) {
      return;
    }
    const clientId = selected.clientId;
    try {
      const result = await uploadAgenticAvatar({ merchantId, file }).unwrap();
      const uploadedSrc = uploadedAvatarSrcFromResponse(result);
      if (!uploadedSrc) {
        toast.error('Failed to upload avatar');
        return;
      }
      updateDraft((prev) => ({
        ...prev,
        personalities: (prev.personalities ?? []).map((personality) =>
          personality.clientId === clientId
            ? { ...personality, avatarSrc: uploadedSrc }
            : personality,
        ),
      }));
      setCustomAvatarByClientId((prev) => ({ ...prev, [clientId]: true }));
      setReplaceAvatarOpenByClientId((prev) => ({ ...prev, [clientId]: false }));
    } catch (err) {
      toast.error(getSaveErrorMessage(err, 'Failed to upload avatar'));
    }
  };

  const syncVoiceSampleToName = (clientId: string, displayName: string) => {
    setVoiceSampleByClientId((prev) => {
      const current = prev[clientId];
      if (current && !isAutoGeneratedVoiceSample(current)) {
        return prev;
      }
      return { ...prev, [clientId]: defaultVoiceSample(displayName) };
    });
  };

  const onCreateAgent = () => {
    if (!canCreate) {
      return;
    }
    const next = emptyPersonality(defaultVoiceProfileId);
    updateDraft((prev) => ({
      ...prev,
      personalities: [...(prev.personalities ?? []), next],
    }));
    setSelectedClientId(next.clientId);
    setVoiceSampleByClientId((prev) => ({
      ...prev,
      [next.clientId]: defaultVoiceSample(next.displayName),
    }));
  };

  const onDeleteSelected = () => {
    if (!canDelete || !selected) {
      return;
    }
    if (selected.isNew) {
      discardPersonality(selected.clientId);
      return;
    }
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!merchantId || !selected?.personalityId || !canDelete) {
      return;
    }
    const clientId = selected.clientId;
    const personalityId = selected.personalityId;
    try {
      await deleteAgenticPersonality({
        merchantId,
        agentName: 'Ella',
        personalityId,
      }).unwrap();
      removePersonality(clientId);
      setIsDeleteModalOpen(false);
      toast.success('Agent deleted');
    } catch (err) {
      toast.error(getSaveErrorMessage(err, 'Failed to delete agent'));
    }
  };

  const handleConfirmEnabled = async (enabled: boolean) => {
    if (!merchantId || !selected?.personalityId || (enabled && !storeReady)) {
      return;
    }
    const clientId = selected.clientId;
    const personalityId = selected.personalityId;
    try {
      await patchAgenticSettings({
        merchantId,
        agentName: 'Ella',
        body: {
          personalities: [
            {
              personalityId,
              enabled,
            },
          ],
        },
      }).unwrap();
      updateDraft((prev) => ({
        ...prev,
        personalities: (prev.personalities ?? []).map((personality) =>
          personality.clientId === clientId ? { ...personality, enabled } : personality,
        ),
      }));
      toast.success(enabled ? 'Agent is now live' : 'Agent is now offline');
      setIsSettingsModalOpen(false);
    } catch (err) {
      toast.error(getSaveErrorMessage(err, 'Failed to update agent settings'));
    }
  };

  const handlePlayVoice = async () => {
    if (!merchantId || !selected?.voiceProfileId || !voiceSample.trim() || isPreviewingVoice) {
      return;
    }

    try {
      const audioBlob = await previewAgenticVoice({
        merchantId,
        agentName: 'Ella',
        body: {
          voiceProfileId: selected.voiceProfileId,
          text: voiceSample.trim(),
        },
      }).unwrap();

      previewAudioRef.current?.pause();
      if (previewAudioRef.current?.src) {
        URL.revokeObjectURL(previewAudioRef.current.src);
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      previewAudioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };
      await audio.play();
    } catch (err) {
      toast.error(getSaveErrorMessage(err, 'Failed to preview voice'));
    }
  };

  return (
    <Box
      data-testid="ella-agents-tab"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: 265 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.6px',
              color: 'text.secondary',
              textTransform: 'uppercase',
            }}
          >
            {`Your agents (${personalities.length})`}
          </Typography>

          {personalities.map((personality) => {
            const isSelected = personality.clientId === selected?.clientId;
            const isLive = personality.enabled;
            const name =
              personality.displayName.trim() || (personality.isNew ? 'New agent' : 'Agent');
            return (
              <Box
                key={personality.clientId}
                component="button"
                type="button"
                onClick={() => setSelectedClientId(personality.clientId)}
                data-testid={`ella-agent-list-item-${personality.clientId}`}
                aria-pressed={isSelected}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  p: 1,
                  pr: 1.5,
                  textAlign: 'left',
                  border: '2px solid',
                  borderColor: isSelected ? '#000000' : '#E0E0E0',
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                <AvatarCircle src={personality.avatarSrc} size={42} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: '24px' }}>
                    {name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '18px',
                      color: isLive ? 'success.main' : 'text.secondary',
                    }}
                  >
                    {isLive ? 'Live on website' : 'Not live on website'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: isLive ? 'success.main' : 'grey.400',
                    flexShrink: 0,
                  }}
                />
              </Box>
            );
          })}

          {showCreateButton && (
            <MuiButton
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={onCreateAgent}
              disabled={!canCreate}
              sx={createButtonSx}
              data-testid="ella-create-agent"
            >
              Create new agent
            </MuiButton>
          )}

          {atLimit && (
            <Typography color="text.secondary" variant="body2" sx={{ m: 0 }}>
              Max number of agents created. Delete one to create a new one.
            </Typography>
          )}

          {personalities.length >= 1 && hasLiveAgent ? (
            <Box
              sx={{
                width: '100%',
                mt: 2,
                pt: 2.5,
                borderTop: '1px solid',
                borderColor: '#E0E0E0',
              }}
            >
              {liveAgentCount > 1 ? (
                <Typography
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.6px',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  Launcher styling
                </Typography>
              ) : null}
              <AgentSelectorThemeSection
                showDesktopTheme={hasLiveAgent}
                showDesktopSelectorBackgroundColor={liveAgentCount > 1}
                showMobileTheme={hasLiveAgent}
                agentSelector={toAgentSelector(draft.productExperience.agentSelector)}
                personalities={personalities}
                readOnly={readOnly}
                onChange={(agentSelector) =>
                  updateDraft((prev) => ({
                    ...prev,
                    productExperience: { ...prev.productExperience, agentSelector },
                  }))
                }
              />
            </Box>
          ) : null}
        </Box>

        {selected && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {!createMode && <AgentStoreReadinessNotice storeReadiness={storeReadiness} />}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: '#E0E0E0',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
              data-testid="ella-agent-detail"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  minHeight: 48,
                  px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: '#E0E0E0',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    lineHeight: 1,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {selected.displayName.trim() || (selected.isNew ? 'New agent' : 'Agent')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    '& .gc-switch.root.is-checked': {
                      backgroundColor: 'brand.main',
                      borderColor: 'brand.main',
                    },
                    '& .gc-switch.root.is-checked .gc-switch.checked-copy': {
                      color: 'common.black',
                    },
                  }}
                >
                  <Switch
                    isChecked={selectedIsLive}
                    onToggle={(next) => {
                      setPendingEnabled(next);
                      setIsSettingsModalOpen(true);
                    }}
                    isDisabled={!canToggleLive || isSavingEnabled}
                    aria-label="Agent live status"
                    data-testid="ella-agent-detail-live-switch"
                  />
                  <Typography variant="medium">
                    Agent is currently {selectedIsLive ? 'live.' : 'off.'}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  px: 2,
                  py: 2,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>Agent name</Typography>
                  <TextField
                    fullWidth
                    placeholder="Agent name"
                    value={selected.displayName}
                    onChange={(e) => updateSelected({ displayName: e.target.value })}
                    onFocus={() => {
                      setVoiceSampleByClientId((prev) => ({
                        ...prev,
                        [selected.clientId]:
                          prev[selected.clientId] ?? defaultVoiceSample(selected.displayName),
                      }));
                    }}
                    onBlur={() => syncVoiceSampleToName(selected.clientId, selected.displayName)}
                    disabled={readOnly}
                    inputProps={{
                      'aria-label': 'Agent name',
                      maxLength: MAX_DISPLAY_NAME_LENGTH,
                    }}
                    data-testid="ella-agent-name"
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
                    Agent avatar
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                  >
                    <FormControl fullWidth disabled={readOnly} sx={{ flex: 1, minWidth: 200 }}>
                      <InputLabel id="ella-agent-avatar-label" shrink>
                        Select an avatar option
                      </InputLabel>
                      <Select
                        labelId="ella-agent-avatar-label"
                        label="Select an avatar option"
                        notched
                        displayEmpty
                        value={avatarSelectValue}
                        onChange={(e: SelectChangeEvent) => onAvatarOptionChange(e.target.value)}
                        renderValue={(value) => {
                          if (value === CUSTOM_AVATAR_OPTION_ID) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AvatarCircle
                                  src={
                                    isCustomAvatarSrc(selected.avatarSrc) ? selected.avatarSrc : ''
                                  }
                                  size={24}
                                  placeholder
                                />
                                Custom avatar
                              </Box>
                            );
                          }
                          if (!value) {
                            return '';
                          }
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AvatarCircle src={value} size={24} />
                              {avatarLabelForSrc(value)}
                            </Box>
                          );
                        }}
                        inputProps={{ 'aria-label': 'Agent avatar' }}
                        data-testid="ella-agent-avatar"
                      >
                        {AGENT_AVATARS.map((option) => (
                          <MenuItem key={option.src} value={option.src}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AvatarCircle src={option.src} size={24} />
                              {option.label}
                            </Box>
                          </MenuItem>
                        ))}
                        <MenuItem value={CUSTOM_AVATAR_OPTION_ID}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AvatarCircle
                              src={isCustomAvatarSrc(selected.avatarSrc) ? selected.avatarSrc : ''}
                              size={24}
                              placeholder
                            />
                            Custom avatar
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ width: 91, textAlign: 'center', flexShrink: 0 }}>
                      <Box sx={{ mx: 'auto', width: 70, position: 'relative' }}>
                        <AvatarCircle
                          src={selected.avatarSrc}
                          size={70}
                          alt="Avatar preview"
                          placeholder={selectedIsCustomAvatar}
                        />
                        {selectedIsCustomAvatar && selected.avatarSrc && !readOnly && (
                          <IconButton
                            aria-label="Edit custom avatar"
                            data-testid="ella-edit-custom-avatar"
                            onClick={() => {
                              setCustomAvatarByClientId((prev) => ({
                                ...prev,
                                [selected.clientId]: true,
                              }));
                              setReplaceAvatarOpenByClientId((prev) => ({
                                ...prev,
                                [selected.clientId]: true,
                              }));
                            }}
                            sx={{
                              position: 'absolute',
                              right: -6,
                              bottom: -6,
                              width: 28,
                              height: 28,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'grey.400',
                              boxShadow: 1,
                              '&:hover': { bgcolor: 'grey.100' },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                      <Typography
                        variant="small"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        Avatar preview
                      </Typography>
                    </Box>
                  </Box>
                  {showCustomAvatarUploader && (
                    <>
                      <Typography
                        variant="small"
                        color="text.secondary"
                        sx={{ mt: 1.5, display: 'block' }}
                      >
                        Upload a custom avatar. 70x70px max, PNG or JPEG.
                      </Typography>
                      <Box sx={{ mt: 2 }} data-testid="ella-custom-avatar-uploader">
                        <LogoInput
                          img={selected.avatarSrc || null}
                          disabled={readOnly || isUploadingAvatar}
                          ariaLabel="Custom avatar"
                          imageAlt="Custom avatar"
                          uploadText="Upload an avatar"
                          replaceText="Replace avatar"
                          removeText="Remove avatar"
                          onChange={(value) => {
                            if (value instanceof File) {
                              void handleCustomAvatarFile(value);
                            }
                          }}
                          onDelete={() => {
                            updateSelected({ avatarSrc: '' });
                            setCustomAvatarByClientId((prev) => ({
                              ...prev,
                              [selected.clientId]: true,
                            }));
                            setReplaceAvatarOpenByClientId((prev) => ({
                              ...prev,
                              [selected.clientId]: true,
                            }));
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Tone</Typography>
                  <Typography
                    variant="small"
                    color="text.secondary"
                    sx={{ mb: 2, display: 'block' }}
                  >
                    Select an option of how your agent should sound.
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 2.5 }}
                    role="radiogroup"
                    aria-label="Tone"
                  >
                    {toneProfiles.map((profile) => {
                      const isSelectedTone = selected.tone === profile;
                      return (
                        <Box
                          key={profile}
                          component="button"
                          type="button"
                          role="radio"
                          aria-checked={isSelectedTone}
                          aria-label={formatToneLabel(profile)}
                          disabled={readOnly}
                          onClick={() => updateSelected({ tone: profile })}
                          sx={{
                            ...selectionBoxSx(isSelectedTone, readOnly),
                            minWidth: 140,
                            flex: 1,
                            height: 52,
                            fontSize: 14,
                            fontWeight: 400,
                            lineHeight: '27px',
                            letterSpacing: 0,
                          }}
                        >
                          {formatToneLabel(profile)}
                        </Box>
                      );
                    })}
                  </Box>
                  <Typography variant="small" fontWeight="bold" sx={{ mb: 1 }}>
                    Example tone: {formatToneLabel(selected.tone)}
                  </Typography>
                  <Box sx={EXAMPLE_BOX_SX}>
                    <Typography variant="small" color="text.secondary">
                      {capitalize(toneDescription)}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Voice</Typography>
                  <Typography
                    variant="small"
                    color="text.secondary"
                    sx={{ mb: 2, display: 'block' }}
                  >
                    Select a voice option for your agent.
                  </Typography>
                  <FormControl fullWidth disabled={readOnly} sx={{ mb: 2 }}>
                    <InputLabel id="ella-agent-voice-label" shrink>
                      Voice
                    </InputLabel>
                    <Select
                      labelId="ella-agent-voice-label"
                      label="Voice"
                      notched
                      value={selected.voiceProfileId}
                      onChange={(e: SelectChangeEvent) =>
                        updateSelected({ voiceProfileId: e.target.value })
                      }
                      inputProps={{ 'aria-label': 'Voice' }}
                      data-testid="ella-agent-voice"
                    >
                      {voiceOptions.map((option) => (
                        <MenuItem key={option.profileId} value={option.profileId}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TextField
                        fullWidth
                        label="Voice sample"
                        value={voiceSample}
                        onChange={(e) =>
                          setVoiceSampleByClientId((prev) => ({
                            ...prev,
                            [selected.clientId]: e.target.value.slice(0, VOICE_SAMPLE_MAX_LENGTH),
                          }))
                        }
                        inputProps={{
                          'aria-label': 'Voice sample',
                          maxLength: VOICE_SAMPLE_MAX_LENGTH,
                        }}
                        helperText={`${voiceSample.length}/${VOICE_SAMPLE_MAX_LENGTH}`}
                        FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
                      />
                    </Box>
                    <Box
                      sx={{
                        height: 56,
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        theme="secondary"
                        text="Play voice"
                        startIcon={<PlayArrowIcon />}
                        dataTestId="ella-play-voice"
                        disabled={
                          !merchantId ||
                          !selected.voiceProfileId ||
                          !voiceSample.trim() ||
                          isPreviewingVoice
                        }
                        loading={isPreviewingVoice}
                        onClick={() => {
                          void handlePlayVoice();
                        }}
                        sx={agentActionButtonSx}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  minHeight: 56,
                  px: 1.5,
                  borderTop: '1px solid',
                  borderColor: '#E0E0E0',
                }}
              >
                {selectedIsLive && (
                  <Typography variant="small" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Agents that are live cannot be deleted.
                  </Typography>
                )}
                <Box sx={{ ml: 'auto' }}>
                  <Button
                    theme="secondary"
                    text="Delete agent"
                    disabled={deleteDisabled}
                    onClick={onDeleteSelected}
                    dataTestId="ella-delete-agent"
                    sx={{ ...agentActionButtonSx, height: 32, minWidth: 132 }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {canToggleLive && (
        <AgentSettingsModal
          open={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
          enabled={selectedIsLive}
          initialEnabled={pendingEnabled}
          isSaving={isSavingEnabled}
          onConfirm={handleConfirmEnabled}
        />
      )}
      <DeleteAgentModal
        open={isDeleteModalOpen}
        agentName={selected?.displayName.trim() || 'Agent'}
        isSaving={isDeletingAgent}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};
